import { Link } from "react-router-dom";
import { ArrowRight, Briefcase, Server, Database, Award, Users, Layers, TrendingUp } from "lucide-react";

const TEAM = [
  {
    name: "Mr. Utkarsh Saxena",
    title: "Director & Chief Executive Officer",
    initials: "US",
    photo: "/team-utkarsh.jpg",
    icon: Briefcase,
    accent: "#8B0000",
    bio: [
      "Over two decades of experience in business consulting and leadership.",
      "Focus on strategic growth, organisational excellence, and performance improvement.",
      "Drives long-term value creation and transformation for our clients.",
    ],
  },
  {
    name: "Mr. Brijesh Nath",
    title: "Technical Lead — SAP, IT & Infrastructure",
    initials: "B",
    photo: "/team-brijesh.jpg",
    icon: Server,
    accent: "#111111",
    bio: [
      "Expert in SAP systems, IT architecture, and enterprise infrastructure.",
      "Designs scalable and efficient technology solutions across the SAP landscape.",
      "Aligns IT systems with business goals to drive operational excellence.",
    ],
  },
];

const VALUE_POINTS = [
  {
    icon: Award,
    title: "Decades of combined experience",
    body: "Senior partners with deep operating backgrounds — every engagement is led personally.",
  },
  {
    icon: Database,
    title: "Strong SAP & IT expertise",
    body: "Certified consultants across HANA, S/4, B1, ByD, BI/BO and infrastructure architecture.",
  },
  {
    icon: Layers,
    title: "Strategic + technical balance",
    body: "We translate boardroom objectives into configured, deployed, and supported systems.",
  },
  {
    icon: TrendingUp,
    title: "Results-driven approach",
    body: "Engagements measured against business outcomes — not slideware or hours billed.",
  },
];

const Team = () => {
  return (
    <div data-testid="team-page">
      {/* HERO */}
      <section
        data-testid="team-hero"
        className="bg-[#F5F5DC] pt-32 pb-24 lg:pt-40 lg:pb-32"
      >
        <div className="container-pro grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <p className="overline mb-6 fade-up">About the Team</p>
            <h1 className="font-display text-5xl lg:text-7xl text-[#111111] leading-[1.05] fade-up-delay-1">
              Meet the leadership
              <br />
              <span className="text-[#8B0000]">driving excellence.</span>
            </h1>
          </div>
          <div className="lg:col-span-5 flex items-end fade-up-delay-2">
            <p className="text-lg text-[#4A4A4A] leading-relaxed">
              Decades of combined experience across business consulting, SAP
              architecture, and enterprise transformation — partnering with
              clients who demand more than advice.
            </p>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section data-testid="team-intro-section" className="section bg-white">
        <div className="container-pro max-w-4xl">
          <p className="overline mb-5">Our leadership</p>
          <h2 className="font-display text-3xl lg:text-4xl text-[#111111] leading-tight">
            A senior team that pairs strategic clarity with technical depth —
            <span className="text-[#8B0000]"> and stays accountable to outcomes.</span>
          </h2>
          <p className="text-[#4A4A4A] leading-relaxed mt-6 text-lg">
            Anila Consulting is led by practitioners who have spent careers at
            the intersection of strategy, SAP and operations. Every engagement
            is led personally by a member of our leadership — no junior
            hand-offs, no anonymous research pools, just experienced people
            doing the work.
          </p>
        </div>
      </section>

      {/* TEAM CARDS */}
      <section data-testid="team-cards-section" className="section bg-[#F5F5DC]">
        <div className="container-pro">
          <div className="max-w-3xl mb-16">
            <p className="overline mb-5">The team</p>
            <h2 className="font-display text-4xl lg:text-5xl text-[#111111] leading-tight">
              The people you'll
              <br />
              <span className="text-[#8B0000]">work with directly.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {TEAM.map((member, i) => {
              const Icon = member.icon;
              return (
                <article
                  key={member.name}
                  data-testid={`team-card-${member.name}`}
                  className="team-card bg-white border border-[#E5E5E5] p-8 lg:p-10 flex flex-col transition-all duration-300 hover:-translate-y-1.5 hover:shadow-2xl group fade-up"
                  style={{ animationDelay: `${i * 120}ms` }}
                >
                  {/* Headshot */}
                  <div className="relative aspect-[4/5] mb-8 overflow-hidden bg-[#111111]">
                    {member.photo ? (
                      <img
                        src={member.photo}
                        alt={member.name}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
                      />
                    ) : (
                      <div
                        className="absolute inset-0 flex items-center justify-center transition-transform duration-500 group-hover:scale-[1.05]"
                        style={{
                          background: `linear-gradient(135deg, #1a1a1a 0%, ${member.accent} 200%)`,
                        }}
                      >
                        <span className="font-display text-white text-7xl lg:text-8xl tracking-tight opacity-90">
                          {member.initials}
                        </span>
                      </div>
                    )}
                    <div className="absolute bottom-4 left-4 w-12 h-12 bg-white/95 flex items-center justify-center">
                      <Icon size={22} strokeWidth={1.5} className="text-[#8B0000]" />
                    </div>
                    <div className="absolute top-4 right-4 overline text-[10px] tracking-[0.25em] text-white/95 bg-black/40 px-2 py-1">
                      {`0${i + 1}`}
                    </div>
                  </div>

                  <h3 className="font-display text-2xl text-[#111111] mb-2 transition-colors duration-300 group-hover:text-[#8B0000]">
                    {member.name}
                  </h3>
                  <p className="text-xs uppercase tracking-[0.18em] text-[#8B0000] font-semibold mb-6">
                    {member.title}
                  </p>

                  <ul className="space-y-3 text-[#4A4A4A] leading-relaxed">
                    {member.bio.map((line, idx) => (
                      <li
                        key={idx}
                        className="flex gap-3 text-[15px]"
                      >
                        <span className="text-[#8B0000] mt-2 flex-shrink-0 w-1.5 h-1.5 bg-[#8B0000] rounded-full" />
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* VALUE / WHY THIS TEAM */}
      <section
        data-testid="team-value-section"
        className="section bg-white"
      >
        <div className="container-pro">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <p className="overline mb-5">Why this team</p>
              <h2 className="font-display text-4xl lg:text-5xl text-[#111111] leading-tight">
                What sets our
                <br />
                <span className="text-[#8B0000]">leadership apart.</span>
              </h2>
              <p className="text-[#4A4A4A] mt-6 leading-relaxed">
                The combination of strategic perspective and hands-on SAP
                expertise is rare — and it's exactly what complex
                transformations require.
              </p>
            </div>

            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E5E5E5] border border-[#E5E5E5]">
                {VALUE_POINTS.map((v, i) => {
                  const Icon = v.icon;
                  return (
                    <div
                      key={v.title}
                      data-testid={`team-value-${i}`}
                      className="bg-white p-8 group hover:bg-[#FAF7EE] transition-colors duration-300"
                    >
                      <Icon
                        size={28}
                        strokeWidth={1.5}
                        className="text-[#8B0000] mb-5 transition-transform duration-300 group-hover:translate-x-1"
                      />
                      <h3 className="font-display text-xl text-[#111111] mb-3 leading-snug">
                        {v.title}
                      </h3>
                      <p className="text-[#4A4A4A] leading-relaxed text-sm">
                        {v.body}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        data-testid="team-cta-section"
        className="bg-[#8B0000] text-white py-24 lg:py-32"
      >
        <div className="container-pro grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <p className="overline mb-5" style={{ color: "rgba(255,255,255,0.85)" }}>
              Begin the conversation
            </p>
            <h2 className="font-display text-4xl lg:text-6xl leading-tight text-white">
              Work with experts who
              <br />
              understand your business.
            </h2>
            <p className="text-white/80 mt-6 text-lg max-w-2xl leading-relaxed">
              Tell us about your SAP landscape and the outcome you're chasing.
              A senior partner will respond personally, with a clear plan —
              not a template.
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link
              to="/contact"
              data-testid="team-cta-button"
              className="btn-ghost-light"
            >
              Get in Touch <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Team;
